import { Marked, Parser, Renderer, Tokens } from 'marked';
import markedKatex from 'marked-katex-extension';
import katex from 'katex';
import { markedTexMath } from './texMathExtension';

// Minimal HTML escaping for code/codespan contexts
export function escapeHtml(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

// For text nodes (e.g., code contents), quotes don't need escaping
export function escapeHtmlText(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

// Helper to parse tokens using the current renderer's parser
function parseInline(this: Renderer, tokens: Tokens.Generic[]): string {
    return (this.parser as Parser).parseInline(tokens);
}

function parseBlock(this: Renderer, tokens: Tokens.Generic[]): string {
    return (this.parser as Parser).parse(tokens);
}

// Custom renderer for predictable export HTML, mirroring Chat.tsx spacing semantics
const htmlExportRenderer = {
    // --- Block-level Elements ---
    heading(this: Renderer, { tokens, depth }: Tokens.Heading): string {
        const text = parseInline.call(this, tokens);
        return text ? `<h${depth}>${text}</h${depth}>` : '';
    },

    paragraph(this: Renderer, { tokens }: Tokens.Paragraph): string {
        const text = parseInline.call(this, tokens).trim();
        return text ? `<p>${text}</p>` : '';
    },

    list(this: Renderer, { ordered, start, items }: Tokens.List): string {
        const children = items
            .map((it) => this.listitem!(it as Tokens.ListItem))
            .join('');
        if (ordered) {
            const startAttr =
                typeof start === 'number' && start > 1
                    ? ` start="${start}"`
                    : '';
            return `<ol${startAttr}>${children}</ol>`;
        }
        return `<ul>${children}</ul>`;
    },

    listitem(
        this: Renderer,
        { tokens, task, checked }: Tokens.ListItem,
    ): string {
        // Render child blocks inline-safe so paragraphs inside li are preserved
        const inner = parseBlock.call(this, tokens);
        if (task) {
            const box = `<input type="checkbox" disabled ${checked ? 'checked' : ''} />`;
            return `<li>${box} ${inner}</li>`;
        }
        return `<li>${inner}</li>`;
    },

    blockquote(this: Renderer, { tokens }: Tokens.Blockquote): string {
        const inner = parseBlock.call(this, tokens);
        return `<blockquote>${inner}</blockquote>`;
    },

    code(this: Renderer, { text, lang }: Tokens.Code): string {
        const cls = lang ? ` class="language-${lang}"` : '';
        return `<pre><code${cls}>${escapeHtmlText(text)}</code></pre>`;
    },

    table(this: Renderer, { header, rows }: Tokens.Table): string {
        const parseRow = (r: Tokens.TableCell[]) =>
            r.map((c) => parseInline.call(this, c.tokens)).join('</td><td>');
        const thead = `<thead><tr><th>${parseRow(header)}</th></tr></thead>`;
        const tbody = `<tbody>${rows.map((r) => `<tr><td>${parseRow(r)}</td></tr>`).join('')}</tbody>`;
        return `<div class="table-wrapper"><table>${thead}${tbody}</table></div>`;
    },

    hr(): string {
        return '<hr />';
    },

    // --- Inline-level Elements ---
    strong(this: Renderer, { tokens }: Tokens.Strong): string {
        return `<strong>${parseInline.call(this, tokens)}</strong>`;
    },

    em(this: Renderer, { tokens }: Tokens.Em): string {
        return `<em>${parseInline.call(this, tokens)}</em>`;
    },

    del(this: Renderer, { tokens }: Tokens.Del): string {
        return `<del>${parseInline.call(this, tokens)}</del>`;
    },

    codespan({ text }: Tokens.Codespan): string {
        return `<code>${escapeHtmlText(text)}</code>`;
    },

    link(this: Renderer, { href, title, text }: Tokens.Link): string {
        const t = title ? ` title="${escapeHtml(title)}"` : '';
        const h = href ? ` href="${escapeHtml(href)}"` : '';
        return `<a${h}${t}>${escapeHtmlText(text || '')}</a>`;
    },

    image({ href, text }: Tokens.Image): string {
        const alt = escapeHtml(text || '');
        const src = escapeHtml(href || '');
        return `<img src="${src}" alt="${alt}" />`;
    },

    br(): string {
        return '<br />';
    },

    html(this: Renderer, token: Tokens.HTML | Tokens.Tag): string {
        // Pass-through trusted inline HTML (assuming content already sanitized upstream)
        return token.text;
    },
};

// Exported Marked instance using htmlExportRenderer for HTML/pdf export
export const htmlExportMarked = new Marked({
    renderer: htmlExportRenderer as unknown as Renderer,
    useNewRenderer: true,
    gfm: true,
    breaks: false,
});

// Register katex tokenizers (markedKatex: $/$$ ; markedTexMath: \[/\()
htmlExportMarked.use(markedKatex({ throwOnError: false }));
htmlExportMarked.use(markedTexMath());
// Explicit renderers for blockKatex/inlineKatex — required because markedTexMath registers
// its tokenizers under different extension names (texBlockMath, texInlineMath, etc.) while
// emitting tokens typed blockKatex/inlineKatex. Without this, those tokens miss the
// markedKatex renderer and fall through to a raw-text fallback, causing duplication.
htmlExportMarked.use({
    extensions: [
        {
            name: 'blockKatex',
            level: 'block' as const,
            renderer(token: Tokens.Generic): string {
                const html = katex.renderToString(token.text, {
                    throwOnError: false,
                    displayMode: true,
                });
                return `<div class="math-block">${html}</div>`;
            },
        },
        {
            name: 'inlineKatex',
            level: 'inline' as const,
            renderer(token: Tokens.Generic): string {
                return katex.renderToString(token.text, {
                    throwOnError: false,
                    displayMode: (token as any).displayMode ?? false,
                });
            },
        },
    ],
});
